import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message } from "antd";
import { createCurrency, createCurrencyAlias, getAllCurrencies, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const CurrencyComponent = ({ exception, onClose, onResolved }) => {
    const [currencyName, setCurrencyName] = useState(null);
    const [currencyIso, setCurrencyIso] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [currencies, setCurrencies] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const extractCurrencyDetails = () => {
            setCurrencyName(exception?.unknown_infomation);
            setCurrencyIso(exception?.unknown_infomation || '');
        };

        if (exception && exception.description) {
            extractCurrencyDetails();
        }

        const fetchCurrencies = async () => {
            setLoading(true);
            try {
                const response = await getAllCurrencies();
                const currenciesData = response?.data?.data || [];
                setCurrencies(Array.isArray(currenciesData) ? currenciesData : []);
            } catch (error) {
                console.error('Failed to fetch currencies', error);
                message.error('Failed to fetch currencies');
            } finally {
                setLoading(false);
            }
        };

        fetchCurrencies();
    }, [exception]);

    const handleMarkAsNew = async () => {
        setLoading(true);
        if (currencyName) {
            if (exception.status === 'pending') {
                try {
                    const payload = {
                        id: 0,
                        name: currencyName,
                        iso: currencyIso || currencyName.substring(0, 3).toUpperCase(),
                        alias_for: 0,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    };
                    
                    await createCurrency(payload);
                    await updateException(exception.id);
                    console.log(`Currency "${currencyName}" created successfully.`);
                    message.success('Currency created');
                    try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                    onClose();
                } catch (error) {
                    console.error(`Failed to create currency "${currencyName}".`, error);
                    message.error('Failed to create currency');
                } finally {
                    setLoading(false);
                }
            } else {
                console.warn("Exception already resolved.");
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            console.warn("Currency name not found.");
            setLoading(false);
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleCurrencySelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        setLoading(true);
        if (exception.status === 'pending') {
            try {
                const payload = {
                    name: currencyName,
                    iso: currencyIso || currencyName.substring(0, 3).toUpperCase(),
                    alias_for: selectedAliasId
                };
                
                await createCurrencyAlias(payload);
                await updateException(exception.id);
                console.log(`Currency "${currencyName}" set as alias for currency with ID: ${selectedAliasId}`);
                message.success('Alias saved');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } catch (error) {
                console.error(`Failed to update alias for currency with ID: ${selectedAliasId}`, error);
                message.error('Failed to save alias');
            } finally {
                setLoading(false);
            }
        } else {
            console.warn("Exception already resolved.");
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    {currencyName ? `Select an existing currency to alias "${currencyName}" to.` : 'Select an existing currency to alias.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a currency"
                                    onChange={handleCurrencySelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {currencies.map((currency) => (
                                        <Option key={currency.id} value={currency.id}>
                                            {currency.name} ({currency.iso})
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default CurrencyComponent;
